import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Discovery } from "@smash-sdk/discovery/02-2023";
import { RootState } from '../../store';
import { config, Region } from '@smash-sdk/core';
import { AsyncState } from '../../shared/models/async.interface';
import { Billing } from "@smash-sdk/billing/01-2022";
import { isApiPlan } from "../../shared/helpers/helpers";
import { logout } from '../auth/authSlice';
const { services } = require('../../' + process.env.REACT_APP_SMASH_SERVICES as string);

const discovery = new Discovery({ host: process.env.REACT_APP_BASE_DISCOVERY_API });

export interface DiscoveryState extends AsyncState {
    services: {
        region: Service[];
        name: string;
        ttl: number;
        url: string;
    }[];
    region: string | null;
    country: string | null;
    countryCode: string | null;
}

export interface Service {
    service: string;
    region: string;
    name: string;
    url: string;
}

const setHostsUrls = ({ services }: { services: Service[] }): void => {
    const libs = new Set(services.map(service => service.name));
    libs.forEach(lib => {
        const libServices = services.filter(service => service.name === lib);
        const urlDic = {} as any;
        libServices.forEach(({ url, region }) => {
            urlDic[region] = url;
        });
        config.setHosts(lib, urlDic);
    });
}

export const listServices = createAsyncThunk(
    'discovery/listServices',
    async (_, thunkApi) => {
        try {
            const response = await discovery.listPublicServices();

            config.setRegion(response.region as Region);

            setHostsUrls({ services });

            const { auth }: any = thunkApi.getState();
            const { identity, isAuthenticated } = auth;

            if (isAuthenticated && identity?.region) {
                config.setToken(identity.token.token);
                config.setRegion(identity.region as Region);
                const billing = new Billing();
                const { subscription } = await billing.getSubscription();

                if (!isApiPlan(subscription.plan)) {
                    thunkApi.dispatch(logout());
                    throw new Error(`notCompatible`);
                }
            } else {
                config.setRegion(response.region as Region);
            }

            return { ...response, services };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const initialState: DiscoveryState = {
    services: [],
    region: null,
    country: null,
    countryCode: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
}

export const discoverySlice = createSlice({
    name: 'discovery',
    initialState,
    reducers: {
        resetDiscovery: (state) => {
            state = {
                services: state.services,
                region: null,
                country: null,
                countryCode: null,
                isLoading: false,
                isSuccess: false,
                isError: false,
            }
            return state
        },
    },
    extraReducers: (builder) => {
        builder
            // LIST SERVICES
            .addCase(listServices.pending, (state) => {
                state.isLoading = true;
                state.error = undefined;
            })
            .addCase(listServices.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.services = action.payload.services;
                state.region = action.payload.region;
                state.country = action.payload.country;
                state.countryCode = action.payload.countryCode;
            })
            .addCase(listServices.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.services = [];
                state.error = action.error;
            })
    }
});



export const { resetDiscovery } = discoverySlice.actions;

export const discoveryState = (state: RootState) => state.discovery;

export default discoverySlice.reducer;